'use client'

import { useState } from 'react';
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface InventorySectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

interface PricingRow {
  adType: string;
  size: string;
  week1: string;
  week4: string;
  week8: string;
  week12: string;
  week26: string;
  highlight?: 'yellow' | 'blue';
}

const tabsData = [
  {
    id: 'windycitytimes',
    label: 'WindyCityTimes.com',
    data: [
      { adType: 'Billboard', size: '970 x 250 pixels', week1: '$300', week4: '$1000', week8: '$1,800', week12: '$2,550', week26: '$5,850', highlight: 'yellow' as const },
      { adType: 'Leaderboard', size: '728 x 90 pixels', week1: '$250', week4: '$800', week8: '$1,440', week12: '$2,040', week26: '$4,680', highlight: 'yellow' as const },
      { adType: 'Med. Rectangle', size: '300 x 250 pixels', week1: '$200', week4: '$500', week8: '$900', week12: '$1,275', week26: '$2,925', highlight: 'yellow' as const },
      { adType: 'Filmstrip (MPU)', size: '300 x 600 pixels', week1: '$400', week4: '$700', week8: '$1,260', week12: '$1,785', week26: '$4,095', highlight: 'yellow' as const },
      { adType: 'Social Media**', size: '', week1: '$100', week4: '$350', week8: '$600', week12: '', week26: '', highlight: 'blue' as const },
    ]
  },
  {
    id: 'chicagosocialbutterflies',
    label: 'ChicagoSocialButterflies.com',
    data: [
      { adType: 'Billboard', size: '970 x 250 pixels', week1: '$250', week4: '$850', week8: '$1,500', week12: '$2,100', week26: '$4,800', highlight: 'yellow' as const },
      { adType: 'Leaderboard', size: '728 x 90 pixels', week1: '$200', week4: '$650', week8: '$1,150', week12: '$1,600', week26: '$3,700', highlight: 'yellow' as const },
      { adType: 'Med. Rectangle', size: '300 x 250 pixels', week1: '$150', week4: '$400', week8: '$720', week12: '$1,000', week26: '$2,300', highlight: 'yellow' as const },
      { adType: 'Filmstrip (MPU)', size: '300 x 600 pixels', week1: '$300', week4: '$550', week8: '$990', week12: '$1,400', week26: '$3,200', highlight: 'yellow' as const },
      { adType: 'Social Media**', size: '', week1: '$75', week4: '$275', week8: '$475', week12: '', week26: '', highlight: 'blue' as const },
    ]
  },
  {
    id: 'newsletters',
    label: 'Email Newsletters',
    data: [
      { adType: 'Banner Ad', size: '600 x 100 pixels', week1: '$150', week4: '$500', week8: '$900', week12: '$1,200', week26: '$2,700', highlight: 'yellow' as const },
      { adType: 'Sponsored Content', size: 'Text + Image', week1: '$300', week4: '$1,000', week8: '$1,800', week12: '$2,500', week26: '$5,500', highlight: 'yellow' as const },
      { adType: 'Dedicated Email', size: 'Full Send', week1: '$500', week4: '$1,800', week8: '$3,200', week12: '$4,500', week26: '$10,000', highlight: 'yellow' as const },
    ]
  }
];

function PricingTable({ 
  data, 
  theme, 
}: { 
  data: PricingRow[]; 
  theme: any; 
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}></th>
            <th className="text-center py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>1-week</th>
            <th className="text-center py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>4-weeks</th>
            <th className="text-center py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>8-weeks</th>
            <th className="text-center py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>12-weeks</th>
            <th className="text-center py-3 px-4 font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>26-weeks</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td className="py-3 px-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <div className="font-semibold font-primary" style={{ color: theme.text }}>{row.adType}</div>
                {row.size && (
                  <div 
                    className="text-sm px-2 py-1 mt-1 inline-block rounded font-primary font-bold"
                    style={{ 
                      background: 'linear-gradient(to right, rgb(237, 34, 36), rgb(243, 91, 34), rgb(249, 150, 33), rgb(245, 193, 30), rgb(241, 235, 27) 27%, rgb(241, 235, 27), rgb(241, 235, 27) 33%, rgb(99, 199, 32), rgb(12, 155, 73), rgb(33, 135, 141), rgb(57, 84, 165), rgb(97, 55, 155), rgb(147, 40, 142))',
                      color: '#fff'
                    }}
                  >
                    {row.size}
                  </div>
                )}
              </td>
              <td className="py-3 px-4 text-center font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week1}</td>
              <td className="py-3 px-4 text-center font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week4}</td>
              <td className="py-3 px-4 text-center font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week8}</td>
              <td className="py-3 px-4 text-center font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week12 || '-'}</td>
              <td className="py-3 px-4 text-center font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week26 || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InventorySection({ 
  surface, 
  theme, 
  radius, 
  fontFamily, 
  gradStart, 
  gradEnd, 
  angle,
  content
}: InventorySectionProps) {
  const [activeTab, setActiveTab] = useState('windycitytimes');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const activeTabData = tabsData.find(tab => tab.id === activeTab);

  return (
    <section id="inventory" className="relative w-full py-16" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 ${scrollAnimationClasses.transition} ${headerVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
        >
          <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-primary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Tabs */}
        <div 
          ref={tableRef as React.RefObject<HTMLDivElement>}
          className={`${scrollAnimationClasses.transition} ${tableVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          style={{ transitionDelay: '100ms' }}
        >
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabsData.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-6 py-3 text-sm font-semibold transition-all duration-200 hover:scale-105 font-primary"
              style={{
                backgroundColor: activeTab === tab.id ? '#F5CC5C' : 'transparent',
                color: activeTab === tab.id ? '#000' : theme.text,
                border: `2px solid ${activeTab === tab.id ? '#F5CC5C' : theme.border}`,
                borderRadius: radius
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Pricing Table */}
        <div 
          className="p-8 shadow-sm" 
          style={{
            backgroundColor: surface === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
            border: `1px solid ${theme.border}`,
            borderRadius: radius
          }}
        >
          {activeTabData && (
            <PricingTable 
              data={activeTabData.data} 
              theme={theme} 
            />
          )}
          
          <p className="text-sm mt-6 font-primary" style={{ color: theme.mutedText }}>
            ** Social Media pricing includes posts on our social media channels promoting your brand or event.
          </p>
        </div>
        </div>
      </div>
    </section>
  );
}
